import { useEffect, useRef, useState } from 'react'
import { useSearchParams, useNavigate, Link } from 'react-router-dom'
import { useAuthStore, waitForAuthHydration } from '@/store'
import { authApi, ApiErrorResponse } from '@/services/api'
import type { VerifySubstitutionTokenResponse } from '@/services/api/auth'
import { applyMagicSession, toInternalPath } from '@/utils/authRedirect'

const PENDING_REDIRECT_KEY = 'pending_substitution_redirect'
const FALLBACK_INCOMING = '/teacher/incoming-requests'

export function SubstitutionRequestLandingPage() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const attemptedRef = useRef(false)

  const [status, setStatus] = useState<'loading' | 'expired' | 'invalid' | 'success'>('loading')
  const [teacherEmail, setTeacherEmail] = useState<string | null>(null)
  const [redirectUrl, setRedirectUrl] = useState<string | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [loginEmailSent, setLoginEmailSent] = useState(false)
  const [sendingLoginEmail, setSendingLoginEmail] = useState(false)
  const [loginEmailError, setLoginEmailError] = useState<string | null>(null)

  useEffect(() => {
    const token = searchParams.get('token')

    if (!token) {
      setStatus('invalid')
      setErrorMessage('Kein Token gefunden.')
      return
    }
    if (attemptedRef.current) return
    attemptedRef.current = true

    async function handleVerify() {
      if (!token) return

      try {
        await waitForAuthHydration()
        const response: VerifySubstitutionTokenResponse = await authApi.verifySubstitutionToken(token)
        const path = toInternalPath(response.redirect_url, FALLBACK_INCOMING)

        if (response.valid && response.redirect_url) {
          const ok = await applyMagicSession(response)
          if (!ok) {
            setStatus('invalid')
            setErrorMessage('Anmeldung fehlgeschlagen.')
            return
          }
          setStatus('success')
          setRedirectUrl(path)
          setTimeout(() => navigate(path), 1200)
          return
        }

        if (!response.valid && response.redirect_url) {
          if (useAuthStore.getState().isAuthenticated) {
            setStatus('success')
            setRedirectUrl(path)
            setTimeout(() => navigate(path), 1200)
            return
          }
          if (response.request_id) {
            localStorage.setItem(PENDING_REDIRECT_KEY, JSON.stringify({ request_id: response.request_id }))
          }
          setStatus('expired')
          setTeacherEmail(response.teacher_email || null)
          return
        }

        if (response.valid === false && response.expired) {
          setStatus('expired')
          setTeacherEmail(response.teacher_email || null)
          if (response.request_id) {
            localStorage.setItem(
              PENDING_REDIRECT_KEY,
              JSON.stringify({ request_id: response.request_id }),
            )
          }
          return
        }

        setStatus('invalid')
        setErrorMessage(response.error || 'Link ungültig.')
      } catch (e: unknown) {
        setStatus('invalid')
        if (e instanceof ApiErrorResponse) {
          setErrorMessage(e.detail || 'Fehler bei der Token-Verifizierung')
        } else if (e instanceof Error) {
          setErrorMessage(e.message)
        } else {
          setErrorMessage('Unbekannter Fehler')
        }
      }
    }

    handleVerify()
  }, [searchParams, navigate])

  const handleResendLoginEmail = async () => {
    if (!teacherEmail) return
    setSendingLoginEmail(true)
    setLoginEmailError(null)
    try {
      await authApi.requestLogin({ email: teacherEmail, frontend_url: window.location.origin })
      setLoginEmailSent(true)
    } catch (e: unknown) {
      if (e instanceof ApiErrorResponse) {
        setLoginEmailError(e.detail || 'E-Mail konnte nicht gesendet werden')
      } else if (e instanceof Error) {
        setLoginEmailError(e.message)
      } else {
        setLoginEmailError('Unbekannter Fehler')
      }
    } finally {
      setSendingLoginEmail(false)
    }
  }

  if (status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full text-center py-8">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">Anfrage-Link wird geprüft...</p>
        </div>
      </div>
    )
  }

  if (status === 'success') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full text-center py-8">
          <div className="text-green-500 text-6xl mb-4">✓</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Erfolgreich angemeldet</h1>
          <p className="text-gray-600 mb-6">Sie werden gleich zur Anfrage weitergeleitet...</p>
          {redirectUrl && (
            <Link
              to={redirectUrl}
              className="inline-block bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium"
            >
              Jetzt zur Anfrage
            </Link>
          )}
        </div>
      </div>
    )
  }

  if (status === 'expired') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full text-center py-8">
          <div className="text-amber-500 text-6xl mb-4">⏰</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Link abgelaufen</h1>
          <p className="text-gray-600 mb-6">
            Dieser Anfrage-Link ist abgelaufen. Bitte loggen Sie sich ein — Sie werden danach automatisch zur Anfrage weitergeleitet.
          </p>
          {loginEmailSent ? (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
              <p className="text-green-800 font-medium mb-1">Login-E-Mail gesendet</p>
              <p className="text-green-700 text-sm">Bitte prüfen Sie Ihr Postfach und klicken Sie auf den Login-Link.</p>
            </div>
          ) : (
            <>
              {teacherEmail && (
                <button
                  onClick={handleResendLoginEmail}
                  disabled={sendingLoginEmail}
                  className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:opacity-50"
                >
                  {sendingLoginEmail ? 'Wird gesendet...' : `Login-Link an ${teacherEmail} senden`}
                </button>
              )}
              {loginEmailError && <p className="text-red-600 text-sm mt-2">{loginEmailError}</p>}
            </>
          )}
          <div className="mt-6">
            <Link to="/login" className="text-blue-700 hover:text-blue-800 text-sm">Oder manuell zum Login</Link>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full text-center py-8">
        <div className="text-red-500 text-6xl mb-4">✗</div>
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Link ungültig</h1>
        <p className="text-gray-600 mb-6">{errorMessage || 'Dieser Anfrage-Link ist ungültig oder wurde widerrufen.'}</p>
        <div className="space-x-4">
          <Link to="/login" className="inline-block bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium">Zum Login</Link>
          <Link to="/" className="inline-block bg-gray-200 text-gray-800 px-6 py-3 rounded-lg hover:bg-gray-300 transition-colors font-medium">Zur Startseite</Link>
        </div>
      </div>
    </div>
  )
}
